import React from 'react';
import { useL1ObjectDetailModel } from './L1ObjectDetailModel';
import { Switch, Route, Redirect, Link } from 'react-router-dom';
import { LoadingIndicator } from 'components/LoadingIndicator';
import styles from './l1ObjectDetail.module.scss';
import i18n from 'i18n';
import _ from 'lodash';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { hasFuncs, notSettleOrder } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { ROUTE_PATH } from 'enum/RoutePath';
import { AddonFeatureManager } from 'core';
import { Order } from 'core/order/Order';
import { InfoDisplaySection } from 'components/InfoDisplaySection/InfoDisplaySection';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { CreativeManagementAction } from './CampaignCreativeManagement/CreativeManagementStateFactory';
import { StateModal } from 'containers/Common/StateModal';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { DownloadReportPanel } from 'components/DownloadReportPanel/DownloadReportPanel';
import { DownloadRtbCampaignGroupReportPanelModel, GroupBy } from 'components/DownloadReportPanel/DownloadReportPanelModel';
import { CreativeErrorsModal } from 'components/CreativeErrorsModal/CreativeErrorsModal';

export const L1ObjectDetail: React.FC<{
  l1ObjectId: number,
  order: Order,
  addonFeatureManager: AddonFeatureManager
}> = ({
  l1ObjectId,
  order,
  addonFeatureManager
}) => {

  const {
    loading,
    viewData,
    currentUrl,
    redirectPath,
    l1Object,
    creativeManageModal,
    componentsData,
    l2ObjectList,
    modifyPermissionItem,
    showDownloadRtbReportModal,
    setShowDownloadRtbReportModal
  } = useL1ObjectDetailModel(l1ObjectId, order, addonFeatureManager);

  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  if (!componentsData || !l1Object) {
    return <LoadingIndicator />;
  }

  const { campaignsComponent, listComponent } = componentsData;
  const renderCampaigns = (props) => {
    const Component = campaignsComponent.component;
    return <Component {...props} {...campaignsComponent.props}/>;
  };

  const renderCampaignList = (props) => {
    const Component = listComponent.component;
    return <Component {...props} {...listComponent.props}/>;
  };

  const renderCreativeManageModal = (props) => {
    if (creativeManageModal) {
      const query = new URLSearchParams(props.location.search);
      const createCreativeErrors: any[] = _.get(props.location.state, 'errors', []);
      const actionParam = query.get('action');
      const permissions = actionParam === CreativeManagementAction.MANAGE ? hasFuncs(Permission.CREATIVE_READ) : hasFuncs(Permission.CREATIVE_READ).and(modifyPermissionItem);
      const renderRedirect = () => <Redirect to={currentUrl} />;
      return (
        <PermissionChecker
          permissionAware={permissions}
          renderOtherwise={renderRedirect}
        >
          <StateModal model={creativeManageModal} />
          {createCreativeErrors && createCreativeErrors.length > 0 && (
            <CreativeErrorsModal errors={createCreativeErrors} />
          )}
        </PermissionChecker>
      );
    }
    return <div/>;
  };

  const renderContent = (props) => {
    if (_.isEmpty(l1Object)) {
      return <LoadingIndicator />;
    }

    return (
      <div className={styles.l1ObjectDetailContainer}>
        {renderCreativeManageModal(props)}
        {loading && <LoadingIndicator />}
        {showDownloadRtbReportModal &&
          <DownloadReportPanel
            model={
              new DownloadRtbCampaignGroupReportPanelModel(
                {
                  name: l1Object.name,
                  l1ObjectId: l1Object.l1ObjectId
                },
                order.startDate,
                order.endDate,
                [GroupBy.CAMPAIGN, GroupBy.CREATIVE],
                _.partial(setShowDownloadRtbReportModal, false),
                addonFeatureManager
              )
            }
          />
        }
        <TitleWithBreadcrumbs
          title={i18n.t<string>('l1Object.labels.detailTitle')}
          routes={[
            { path: '/orders', breadcrumb: i18n.t<string>('orderDetail.labels.title') },
            {
              path: '/orders/:orderNumber',
              breadcrumb: DynamicBreadcrumb,
              props: {
                label: _.get(order, 'projectName'),
                matchParam: 'orderNumber'
              }
            },
            {
              path: '/orders/:orderNumber/campaign-groups/:l1ObjectId',
              breadcrumb: DynamicBreadcrumb,
              props: {
                label: _.get(l1Object, 'name'),
                matchParam: 'l1ObjectId'
              }
            }
          ]}
        />
        <div className={styles.titleBottomLine} />
        <div className={styles.detail}>
          <div className={styles.topArea}>
            <div className={styles.buttonArea}>
              <PermissionChecker permissionAware={modifyPermissionItem.and(notSettleOrder(order))}>
                <Link
                  to={{
                    pathname: `${currentUrl}/edit`,
                    state: {
                      fromDetail: true,
                      l2ObjectList
                    }
                  }}
                >
                  {i18n.t<string>('l1Object.labels.editTitle')}
                </Link>
              </PermissionChecker>
              { (
                  l1Object.channel === L1ObjectChannel.RTB
                ) &&
                <div onClick={_.partial(setShowDownloadRtbReportModal, true)}>
                  {i18n.t<string>('l1Object.labels.downloadReport')}
                </div>
              }
            </div>
          </div>
          <InfoDisplaySection data={viewData.basic} title=''/>
        </div>
        <div className={styles.other}>
          {renderCampaignList(props)}
        </div>
      </div>
    );
  };

  return (
    <Switch>
      <RouteWithPermission
        permissionAware={hasFuncs(Permission.CAMPAIGN_READ).or(modifyPermissionItem)}
        redirectPath={`${currentUrl}/campaigns/${ROUTE_PATH.ERROR403}`}
        path={`${currentUrl}/campaigns`}
        render={renderCampaigns}
      />
      <Route
        render={renderContent}
      />
    </Switch>
  );
};
